import { useState, useEffect, useRef } from "react";
import { Bell, X, Check, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { useAppSelector } from "@/app/hooks";
import { useNotifications, useNotificationStats, useMarkNotificationAsRead } from "@/services/notifications";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useAppSelector((state) => state.auth.user);
  const { data: notificationsData, isLoading } = useNotifications(10, false);
  const { data: stats } = useNotificationStats();
  const markAsRead = useMarkNotificationAsRead();
  const notifications = notificationsData?.data || [];
  const unreadCount = stats?.total_unread ?? 0;

  const notificationsUrl =
    user?.role === "super_admin"
      ? "/super-admin/notifications"
      : user?.role === "agent"
        ? "/agent-portal/notifications"
        : "/admin/notifications";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Prevent underlying body scroll when the full-screen menu overlay is open on mobile devices
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const getDotColor = (type: string) => {
    if (type === "success") return "bg-[hsl(var(--success))]";
    if (type === "warning") return "bg-[hsl(var(--warning))]";
    if (type === "error") return "bg-destructive";
    return "bg-[hsl(var(--info))]";
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === "critical") return "text-[10px] font-semibold text-destructive";
    if (priority === "high") return "text-[10px] font-semibold text-[hsl(var(--warning))]";
    return "text-[10px] font-medium text-muted-foreground";
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative rounded-md p-1.5 text-muted-foreground hover:bg-muted focus:outline-none">
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile Background Backdrop Blur Overlay */}
          <div 
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-xs md:hidden" 
            onClick={() => setOpen(false)}
          />

          {/* Responsive Panel Container Grid Layout */}
          <div className="fixed bottom-0 left-0 right-0 z-50 flex max-h-[80vh] w-full flex-col overflow-hidden rounded-t-xl border border-border bg-card shadow-xl transition-all duration-300 md:absolute md:bottom-auto md:left-auto md:right-0 md:top-10 md:max-h-96 md:w-96 md:rounded-lg">
            
            {/* Header Component Bar Section */}
            <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3 md:py-2.5">
              <p className="text-sm font-semibold text-foreground">Notifications</p>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && <span className="text-xs text-muted-foreground">{unreadCount} unread</span>}
                <button onClick={() => setOpen(false)} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground md:p-0 md:hover:bg-transparent">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Main Notification Dynamic Wrapper Scroll Frame */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">Loading…</div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center px-4 py-12 text-center text-muted-foreground md:py-8">
                  <Inbox className="mb-2 h-6 w-6 opacity-50" />
                  <p className="text-sm">No unread notifications</p>
                  <Link
                    to={notificationsUrl}
                    onClick={() => setOpen(false)}
                    className="mt-2 text-xs text-primary hover:underline"
                  >
                    View history
                  </Link>
                </div>
              ) : (
                notifications.map((n) => (
                  <div key={n.id} className="border-b border-border px-4 py-3.5 last:border-0 hover:bg-muted/50 bg-accent/30 md:py-3">
                    <div className="flex items-start gap-2.5 md:gap-2">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${getDotColor(n.type)}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-foreground truncate">{n.title}</p>
                          <span className={getPriorityBadge(n.priority)}>{n.priority}</span>
                        </div>
                        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground whitespace-normal break-words">
                          {n.message}
                        </p>
                        <div className="mt-2 flex items-center justify-between gap-2 md:mt-1.5">
                          <div className="flex flex-col gap-0.5">
                            <p className="text-[10px] text-muted-foreground">
                              {n.sentBy?.name ? `From: ${n.sentBy.name}` : "System"}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {new Date(n.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <button
                            onClick={() => markAsRead.mutate(n.id)}
                            className="inline-flex min-h-[28px] items-center gap-0.5 rounded-md px-2 py-1 text-[10px] text-primary hover:bg-primary/5 md:min-h-0 md:p-0 md:hover:bg-transparent"
                            title="Mark as read"
                          >
                            <Check className="h-3 w-3" /> Read
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Navigation History Link Section Footer */}
            <div className="shrink-0 border-t border-border bg-card px-4 py-3 text-center md:py-2">
              <Link
                to={notificationsUrl}
                onClick={() => setOpen(false)}
                className="inline-block w-full text-xs font-medium text-primary hover:underline py-1 md:py-0"
              >
                View all notifications
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}